import { DatePipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Dayjs } from 'dayjs';
import * as moment from 'moment-timezone';

/**
 * Versão PURE do `datePipe` (`DateFormatPipe`). Mesma lógica de formatação,
 * mas com `pure: true` — cachea por (value × args × timezone) e só re-executa
 * quando a referência da entrada muda. O `datePipe` original é `pure: false`,
 * o que faz cada CD reformatar TODAS as datas visíveis (people-table + 3
 * colunas de data no event-members × N linhas).
 *
 * Trade-off: se o usuário troca de idioma na sessão, células de data já
 * renderizadas não trocam de formato até a próxima navegação. Aceito.
 */
@Pipe({
  name: 'datePurePipe',
  pure: true,
})
export class DateFormatPurePipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  private argToFormat(currentLang: string, args: string): string {
    switch (currentLang) {
      case 'pt-BR':
      case 'pt-br':
        switch (args) {
          case 'date':           return "dd 'de' MMM yyyy";
          case 'fullDate':       return 'EEEE, d MMMM y';
          case 'dateTime':       return "dd 'de' MMM yyyy HH:mm";
          case 'dateTimeSecond': return "dd 'de' MMM yyyy HH:mm:ss";
          case 'dateTimeZone':   return "dd 'de' MMM yyyy HH:mm z";
          case 'shortTime':      return 'H:mm';
          case 'shortTimeZone':  return 'H:mm z';
          default:               return "dd 'de' MMM yyyy";
        }
      default:
        switch (args) {
          case 'date':           return 'MMM dd, yyyy';
          case 'fullDate':       return 'EEEE, MMMM d, y';
          case 'dateTime':       return 'MMM dd, yyyy HH:mm';
          case 'dateTimeSecond': return 'MMM dd, yyyy HH:mm:ss';
          case 'dateTimeZone':   return 'MMM dd, yyyy HH:mm z';
          case 'shortTime':      return 'h:mm a';
          case 'shortTimeZone':  return 'h:mm a z';
          default:               return 'MMM dd, yyyy';
        }
    }
  }

  transform(value: Dayjs, args: string, timezone?: string): any {
    if (value === null || value === undefined) { return ''; }
    const currentLang = this.translateService.currentLang || 'en';
    const pipe = new DatePipe(currentLang);
    const asString = String(value);
    if (timezone !== undefined) {
      const offset: string = moment.tz(timezone).format('Z');
      return pipe.transform(asString, this.argToFormat(currentLang, args), offset)?.replace('GMT', 'UTC');
    }
    return pipe.transform(asString, this.argToFormat(currentLang, args))?.replace('GMT', 'UTC');
  }
}
